import { motion } from "framer-motion";
import { useState, useEffect, useCallback } from "react";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { ArrowUpRight, Clock } from "lucide-react";
import { toast } from "sonner";

const modules = [
  {
    tag: "M01",
    title: "Strategic Finance Office",
    desc:
      "CFO-grade capital stewardship for principals — from venture allocations to cross-jurisdictional structuring, IC modeling, and portfolio reporting.",
    submodules: [
      {
        slug: "m01-01",
        name: "Venture Capital",
        desc: "Sourcing, diligence, term negotiation, portfolio construction & reporting.",
        detail: {
          read: "30 sec",
          headline: "An institutional venture desk — without the seven-figure fixed cost.",
          what_it_is:
            "A full-stack venture function operated on your behalf: proprietary sourcing across sectors, IC-ready memos, term-sheet negotiation, portfolio construction, and quarterly reporting to your principal.",
          what_you_get: [
            "Proprietary deal pipeline curated to your thesis",
            "IC-grade diligence memos with technology & market reviews",
            "Term-sheet negotiation & closing support",
            "Cap table, MOIC & IRR reporting quarterly",
          ],
          when_to_use:
            "You want disciplined venture exposure without hiring 4–8 investment professionals and spending 12–18 months to steady state.",
        },
      },
      {
        slug: "m01-02",
        name: "Finance Office",
        desc: "Capital planning, cash forecasting, structuring, IC modeling.",
        detail: {
          read: "30 sec",
          headline: "A CFO-grade office wired to your principal.",
          what_it_is:
            "Board-ready capital planning, multi-jurisdictional entity structuring, IC modeling, and consolidated reporting across venture, private, and operating holdings.",
          what_you_get: [
            "Rolling 3-year capital plan & cash forecast",
            "Cross-jurisdictional entity & tax structuring",
            "IC & underwriting models for every allocation",
            "Consolidated principal & auditor reporting",
          ],
          when_to_use:
            "Your holdings span geographies, structures, and asset classes — and the current stack can't produce one true consolidated view.",
        },
      },
    ],
    image:
      "https://images.pexels.com/photos/5103610/pexels-photo-5103610.jpeg",
    accent: "gold",
  },
  {
    tag: "M02",
    title: "Innovation Hub",
    desc:
      "The exploration engine — where emerging technology meets institutional discipline. Deep technical diligence and digital asset infrastructure, staffed by operators.",
    submodules: [
      {
        slug: "m02-01",
        name: "Technology Diligence Center",
        desc: "Code, architecture, security & AI-readiness reviews for direct and LP allocations.",
        detail: {
          read: "30 sec",
          headline: "Look under the hood before you write the cheque.",
          what_it_is:
            "Deep technical diligence for direct deals, LP allocations, and portfolio companies — code, architecture, security, scale, and AI-readiness reviewed by senior engineers.",
          what_you_get: [
            "Codebase & architecture audit with red-flag summary",
            "Security & scale review vs. mandate assumptions",
            "AI-readiness & data-moat assessment",
            "Post-close 30/60/90 uplift roadmap",
          ],
          when_to_use:
            "You are allocating into a tech-heavy company or fund and need a defensible view your CIO and auditor will both accept.",
        },
      },
      {
        slug: "m02-02",
        name: "Digital Asset Infrastructure",
        desc: "Institutional MPC custody, tokenization rails, on-chain treasury, compliance posture.",
        detail: {
          read: "30 sec",
          headline: "Digital assets — treated as core, governed like the rest of your capital.",
          what_it_is:
            "Institutional-grade custody architecture, tokenization rails, on-chain treasury operations, and compliance posture — stood up in weeks, not quarters.",
          what_you_get: [
            "MPC custody with policy-driven approvals",
            "Whitelisting, screening & audit trail",
            "Tokenized T-Bill & yield allocations",
            "Weekly NAV to principal and auditor",
          ],
          when_to_use:
            "Digital assets are on your balance sheet or thesis — and exchange-based custody is no longer an acceptable answer to the board.",
        },
      },
    ],
    image:
      "https://images.pexels.com/photos/10325707/pexels-photo-10325707.png",
    accent: "emerald",
  },
  {
    tag: "M03",
    title: "Sustainability & Impact Desk",
    desc:
      "ESG frameworks and thematic capability across renewable energy, EV battery, and next-gen transport — wired directly into investment memos with impact measurement.",
    submodules: [
      {
        slug: "m03-01",
        name: "ESG Framework",
        desc: "Additionality, impact measurement & disclosure aligned to global standards.",
        detail: {
          read: "30 sec",
          headline: "Impact — measured, additional, and defensible.",
          what_it_is:
            "A working ESG & impact framework aligned to SFDR, IFC, and GIIN — additionality analysis, KPI selection, and disclosure that survives scrutiny.",
          what_you_get: [
            "Mandate-fit impact framework & KPIs",
            "Additionality analysis per allocation",
            "Annual impact report to principal & LPs",
            "Alignment to SFDR / IFC / GIIN standards",
          ],
          when_to_use:
            "You want impact intent to translate into measured outcomes — and reporting that doesn't collapse under an auditor's questions.",
        },
      },
      {
        slug: "m03-02",
        name: "Renewable Energy & Electric Mobility",
        desc: "Solar, wind, grid-scale storage, battery chemistry, supply chain & EV mobility — one integrated coverage team.",
        detail: {
          read: "30 sec",
          headline: "The energy transition, covered end-to-end by one team.",
          what_it_is:
            "Sector-specialist coverage across generation (solar, wind, grid-scale storage) and the electrification stack (battery chemistry, critical-mineral supply, charging & EV mobility) — from origination through portfolio ops.",
          what_you_get: [
            "Unified transition thesis & sector map",
            "Origination pipeline across generation and mobility",
            "Technical, chemistry & supply-chain diligence",
            "PPA / offtake structuring and asset-level reporting",
          ],
          when_to_use:
            "You want serious exposure to the energy transition — not a token allocation — and one team that can tell LFP from NMC and nickel from lithium when it matters.",
        },
      },
    ],
    image:
      "https://images.unsplash.com/photo-1512453979798-5ea266f8880c",
    accent: "gold",
  },
];

export default function Modules() {
  const [hovered, setHovered] = useState(0);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  const openSub = useCallback((moduleIdx, subIdx) => {
    const mod = modules[moduleIdx];
    const sub = mod.submodules[subIdx];
    setHovered(moduleIdx);
    setActive({ mod, sub, subIdx });
    setOpen(true);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#modules/${sub.slug}`);
    }
  }, []);

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v && typeof window !== "undefined") {
      const h = window.location.hash;
      if (h.startsWith("#modules/")) {
        window.history.replaceState(null, "", "#modules");
      }
    }
  };

  useEffect(() => {
    const openFromHash = () => {
      const hash = window.location.hash.replace(/^#/, "");
      const match = hash.match(/^modules\/(m\d{2}-\d{2})$/);
      if (!match) return;
      const slug = match[1];
      for (let i = 0; i < modules.length; i++) {
        const si = modules[i].submodules.findIndex((s) => s.slug === slug);
        if (si >= 0) {
          setTimeout(() => {
            document
              .getElementById("modules")
              ?.scrollIntoView({ behavior: "smooth", block: "start" });
            openSub(i, si);
          }, 400);
          break;
        }
      }
    };
    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, [openSub]);

  return (
    <section
      id="modules"
      className="relative py-24 md:py-40 bg-ink-2 border-t border-[var(--line)]"
      data-testid="modules-section"
    >
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-20">
          <div className="md:col-span-6">
            <div className="text-[10px] tracking-eyebrow uppercase text-gold mb-4">
              IV. Modules
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Three modular desks. <em className="text-emerald not-italic">One</em> or all — combined to the mandate.
            </h2>
          </div>
          <p className="md:col-span-5 md:col-start-8 md:pt-6 text-ivory/70 leading-relaxed">
            Every capability center we operate is composed from these three
            desks — activated individually or stacked into a full principal&rsquo;s office.
            Each is engineered to institutional-grade digital asset infrastructure
            standards — custody, tokenization, on-chain treasury, and audit —
            wired directly into your governance stack.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
          <div className="lg:col-span-7">
            <ul>
              {modules.map((m, i) => (
                <li key={m.tag}>
                  <motion.div
                    onMouseEnter={() => setHovered(i)}
                    onFocus={() => setHovered(i)}
                    tabIndex={0}
                    data-testid={`module-${i}`}
                    className={`group py-8 md:py-10 border-t border-[var(--line)] flex items-start gap-6 md:gap-10 transition-colors outline-none ${
                      hovered === i ? "text-ivory" : "text-ivory/70 hover:text-ivory"
                    }`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.05 }}
                  >
                    <span
                      className={`text-[10px] tracking-eyebrow uppercase mt-3 shrink-0 ${
                        hovered === i ? "text-gold" : "text-ivory-dim"
                      }`}
                    >
                      {m.tag}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between gap-6 flex-wrap">
                        <h3 className="font-display text-3xl md:text-5xl leading-tight">
                          {m.title}
                        </h3>
                        <span
                          className={`text-2xl transition-transform ${
                            hovered === i ? "text-gold translate-x-1" : "text-ivory-dim"
                          }`}
                        >
                          →
                        </span>
                      </div>
                      <p className="mt-3 max-w-2xl text-ivory/60 text-sm md:text-base leading-relaxed">
                        {m.desc}
                      </p>

                      <div className="mt-6 border-l border-[var(--line)] pl-5 space-y-4">
                        <div className="text-[10px] tracking-eyebrow uppercase text-ivory-dim">
                          Sub-modules · tap to open
                        </div>
                        <ul className="space-y-2">
                          {m.submodules.map((s, si) => (
                            <li key={s.name}>
                              <button
                                type="button"
                                onClick={() => openSub(i, si)}
                                data-testid={`submodule-${i}-${si}`}
                                className="group/sub w-full text-left grid grid-cols-12 gap-3 items-baseline py-2 hover:bg-ivory/5 -mx-2 px-2 transition-colors"
                              >
                                <span
                                  className={`col-span-12 md:col-span-4 font-display text-lg md:text-xl leading-tight ${
                                    hovered === i ? "text-ivory" : "text-ivory/85"
                                  }`}
                                >
                                  <span className="text-gold mr-2 text-xs align-middle">
                                    {m.tag}·{String(si + 1).padStart(2, "0")}
                                  </span>
                                  {s.name}
                                  <ArrowUpRight
                                    size={14}
                                    className="inline-block ml-2 opacity-40 group-hover/sub:opacity-100 group-hover/sub:text-gold transition-all group-hover/sub:translate-x-0.5 group-hover/sub:-translate-y-0.5"
                                  />
                                </span>
                                <span className="col-span-12 md:col-span-8 text-xs md:text-sm text-ivory/55 leading-relaxed group-hover/sub:text-ivory/75">
                                  {s.desc}
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                </li>
              ))}
              <li>
                <div className="border-t border-[var(--line)]" />
              </li>
            </ul>
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-28 h-fit">
            <motion.div
              key={hovered}
              initial={{ opacity: 0.4, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
              className="relative overflow-hidden border border-[var(--line)] spotlight"
            >
              <img
                src={modules[hovered].image}
                alt={modules[hovered].title}
                className="w-full h-[380px] md:h-[560px] object-cover"
              />
              <div className="absolute top-4 left-4 text-[10px] tracking-eyebrow uppercase text-ivory z-10">
                {modules[hovered].tag} · {modules[hovered].title}
              </div>
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between z-10 gap-3">
                <div className="text-[10px] tracking-eyebrow uppercase text-ivory/80">
                  {modules[hovered].submodules.length} sub-modules
                </div>
                <div className="text-[10px] tracking-eyebrow uppercase text-ivory-dim">
                  Fig. {String(hovered + 1).padStart(2, "0")}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="right"
          className="!bg-ink !text-ivory border-l border-[var(--line-strong)] w-full sm:!max-w-lg md:!max-w-xl p-0 overflow-y-auto"
          data-testid="submodule-drawer"
        >
          {active && (
            <div className="relative min-h-full flex flex-col">
              <div className="grain absolute inset-0 pointer-events-none" />
              <div className="relative p-8 md:p-10 border-b border-[var(--line)]">
                <div className="text-[10px] tracking-eyebrow uppercase text-gold flex items-center justify-between">
                  <span>
                    {active.mod.tag}·{String(active.subIdx + 1).padStart(2, "0")} · {active.mod.title}
                  </span>
                  <span className="flex items-center gap-1 text-ivory-dim">
                    <Clock size={11} strokeWidth={1.5} />
                    {active.sub.detail.read}
                  </span>
                </div>
                <SheetTitle asChild>
                  <h3 className="mt-4 font-display text-3xl md:text-4xl leading-[1.1] tracking-tight text-ivory">
                    {active.sub.name}
                  </h3>
                </SheetTitle>
                <SheetDescription asChild>
                  <p className="mt-3 text-ivory/70 text-base leading-relaxed">
                    {active.sub.detail.headline}
                  </p>
                </SheetDescription>
              </div>

              <div className="relative p-8 md:p-10 space-y-8 flex-1">
                <section>
                  <div className="text-[10px] tracking-eyebrow uppercase text-ivory-dim mb-3">
                    What it is
                  </div>
                  <p className="text-ivory/80 leading-relaxed text-sm md:text-base">
                    {active.sub.detail.what_it_is}
                  </p>
                </section>

                <section>
                  <div className="text-[10px] tracking-eyebrow uppercase text-ivory-dim mb-3">
                    What you get
                  </div>
                  <ul className="space-y-2.5">
                    {active.sub.detail.what_you_get.map((it) => (
                      <li key={it} className="flex gap-3 text-sm md:text-base text-ivory/85 leading-relaxed">
                        <span className="text-emerald mt-1.5 shrink-0">▸</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                <section className="border-l border-gold/50 pl-4">
                  <div className="text-[10px] tracking-eyebrow uppercase text-gold mb-2">
                    When to use
                  </div>
                  <p className="text-ivory/75 italic leading-relaxed text-sm md:text-base">
                    {active.sub.detail.when_to_use}
                  </p>
                </section>

                <section className="border-t border-[var(--line)] pt-6">
                  <div className="text-[10px] tracking-eyebrow uppercase text-ivory-dim mb-2">
                    Shareable link
                  </div>
                  <div className="flex items-center gap-3">
                    <code className="flex-1 text-xs md:text-sm text-ivory/70 font-mono bg-ink-2 border border-[var(--line)] px-3 py-2 truncate">
                      {typeof window !== "undefined"
                        ? `${window.location.origin}${window.location.pathname}#modules/${active.sub.slug}`
                        : `#modules/${active.sub.slug}`}
                    </code>
                    <button
                      type="button"
                      data-testid="submodule-copy-link"
                      onClick={() => {
                        if (typeof window === "undefined") return;
                        const url = `${window.location.origin}${window.location.pathname}#modules/${active.sub.slug}`;
                        try {
                          if (navigator.clipboard?.writeText) {
                            navigator.clipboard.writeText(url).then(
                              () => toast.success("Link copied to clipboard"),
                              () => toast.success("Link ready: " + url)
                            );
                          } else {
                            toast.success("Link ready: " + url);
                          }
                        } catch (e) {
                          toast.success("Link ready: " + url);
                        }
                      }}
                      className="text-[10px] tracking-eyebrow uppercase text-gold gold-underline shrink-0"
                    >
                      Copy
                    </button>
                  </div>
                </section>
              </div>

              <div className="relative p-6 md:p-8 border-t border-[var(--line)] bg-ink-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="text-[10px] tracking-eyebrow uppercase text-ivory-dim">
                  Confidential · illustrative scope
                </div>
                <a
                  href="#workshop"
                  onClick={() => handleOpenChange(false)}
                  data-testid="submodule-cta"
                  className="group inline-flex items-center gap-2 px-5 py-3 bg-gold text-ink font-medium tracking-wide hover:bg-gold-soft transition-colors text-sm"
                >
                  Discuss this sub-module
                  <ArrowUpRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </a>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
}
